import { EuiEmptyPrompt, EuiPageTemplate, EuiText } from "@inscopix/ideas-eui";
import { Route, Switch, useParams } from "react-router-dom";
import { usePageProjectData } from "./usePageProjectData";
import { ProjectDataProvider } from "./ProjectDataProvider";
import { Page404 } from "pages/404/Page404";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { pollAnalysisResults } from "./PageProject.helpers";
import { ToolSpecProvider } from "providers/ToolSpecProvider/ToolSpecProvider";
import { ProjectStatus } from "types/constants";
import { PageGenericDataTables } from "pages/gdt/PageGenericDataTables";
import { ProjectPermissionProvider } from "./ProjectPermissionProvider";
import { PageProject404 } from "./PageProject404";
import { ModalProvider } from "providers/ModalProvider/ModalProvider";
import { DataTableProvider } from "pages/gdt/store/DataTableProvider";
import { ProjectFoldersProvider } from "pages/gdt/folders/ProjectFoldersProvider";
import AppLoading from "components/AppLoading/AppLoading";

export const PageProject = () => {
  const projectKey = useParams<{ projectKey: string }>().projectKey;
  const tableKey = useParams<{ tableKey: string | undefined }>().tableKey;

  // Poll for analysis results until the component unmounts
  useEffect(() => {
    const stopPolling = pollAnalysisResults(projectKey);
    return stopPolling;
  }, [projectKey]);

  const { loading, error, data } = usePageProjectData({ projectKey });

  if (loading && data === undefined) {
    return <AppLoading />;
  }

  if (error) {
    return <PageProject404 projectKey={projectKey} />;
  }

  const { project } = data;

  if (project.isToolProject) {
    return <Page404 />;
  }

  if (project.status === ProjectStatus.CLONING) {
    return (
      <>
        <Helmet>
          <title>{project.name}</title>
        </Helmet>
        <EuiPageTemplate>
          <EuiPageTemplate>
            <EuiEmptyPrompt
              iconType="copy"
              title={
                <EuiText>
                  <h1>Copying in progress</h1>
                </EuiText>
              }
              body={
                <EuiText>
                  <p>
                    The project you are trying to access is in the process of
                    being copied. Please check back later.
                  </p>
                </EuiText>
              }
            />
          </EuiPageTemplate>
        </EuiPageTemplate>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${project.name}`}</title>
      </Helmet>
      <Switch>
        <ToolSpecProvider>
          <ProjectDataProvider {...data}>
            <ProjectPermissionProvider projectId={project.id}>
              <DataTableProvider projectKey={projectKey} tableKey={tableKey}>
                <ProjectFoldersProvider projectKey={projectKey}>
                  <ModalProvider>
                    <PageGenericDataTables />
                  </ModalProvider>
                </ProjectFoldersProvider>
              </DataTableProvider>
            </ProjectPermissionProvider>
          </ProjectDataProvider>
        </ToolSpecProvider>
        <Route path="*">
          <Page404 />
        </Route>
      </Switch>
    </>
  );
};
